import React, { useEffect, useRef, useState } from 'react';
import { Scrollbars } from '@meitu/saas-ui';
import { usePluginModel } from '@/store/models/plugin-model';
import { useSdkModel } from '@/store/models/sdk-model';
import './more-collapse.less';
import { IMainLayerProps, LayerType, OpenPluginType } from '@meitu/saas-editor-core/es/version-types';
import IconFont from '@meitu/xdesign-icon-font';
import { ExtensionManager } from '@/editor/extensions';
import aiMattingIcon from '@/editor/extensions/ai-matting/assets/aiMatting.svg';
import AiMattingExtension, { AI_MATTING_IMAGE } from '@/editor/extensions/ai-matting/components/ai-matting-extension';
import { events, PLUGIN_CLOSE_EVENT, PLUGIN_OPEN_EVENT } from '@/events-bus';
import { getLocaleStore } from '@/i18n';
import { editor } from '@/instance';
import { isIntl } from '@/react-process-env';
import { isPcPlatform } from '@/utils/auth';
import { checkImageEditor } from '@/utils/common';
import aiExpendIcon from './assets/aiExpend.svg';
import aiModifyIcon from './assets/aiModify.svg';
import aiSizeExtendIcon from './assets/aiSizeExtend.svg';
import qrCodeIcon from './assets/icon-qrcode.svg';
import tableIcon from './assets/icon-table.svg';
import useDisplayAiExtend from './hooks/use-display-ai-extend';
import AiExpendImage, { AI_EXPEND_IMAGE } from './plugins/ai-expend-image/ai-expend-image';
import AiModifyImage, { AI_MODIFY_IMAGE } from './plugins/ai-modify-image/ai-modify-image';
import AiSizeExtend from './plugins/ai-size-extend/ai-size-extend';
import QrCode, { QR_CODE } from './plugins/qr-code/qr-code';
import TablePlayer, { TABLE_PLAYER } from './plugins/table-player/table-player';

const isImageEditor = checkImageEditor();

interface IPluginItem {
    icon: string;
    iconFont?: string;
    title: string | JSX.Element;
    description: string;
    components?: any;
    name: string;
    trackFunc?: string;
    func_id?: string;
    trackEnter?: {
        eventName: string;
        data: any;
    };
    unShow?: boolean;
    extensionInit?: boolean; // 是否执行初始化
}
interface IPluginPanel {
    title: string;
    data: IPluginItem[];
    onClick?: (data: IPluginItem) => void;
    iconClick?: (data: IPluginItem) => void;
}

const PluginPanel: React.FC<IPluginPanel> = ({ title, data, onClick, iconClick }) => {
    return (
        <div className="c-more-collapse__panel">
            <div className="c-more-collapse__panel-title">{title}</div>
            <div className="c-more-collapse__panel-content">
                {data.map((item, index) => {
                    if (item.unShow) {
                        return null;
                    }

                    return (
                        <div
                            key={index}
                            className="c-more-collapse__panel-content-item"
                            onClick={() => {
                                onClick?.(item);
                            }}
                        >
                            <div className="c-more-collapse__panel-content-icon">
                                <img src={item.icon} alt="" />
                            </div>
                            <div className="c-more-collapse__panel-content-body">
                                <div className="c-more-collapse__panel-content-title">
                                    {item.title}
                                    {item.iconFont && !isPcPlatform() ? (
                                        <span
                                            className="title-icon"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                iconClick?.(item);
                                            }}
                                        >
                                            <IconFont fontSize={14} name={item.iconFont}></IconFont>
                                        </span>
                                    ) : null}
                                </div>
                                <div className="c-more-collapse__panel-content-des">{item.description}</div>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

const PLUGINS_CONFIGS = (
    displayAiExtend = false,
    disableAiGenerateImage = false,
): { title: any; data: IPluginItem[] }[] => {
    const { $t } = getLocaleStore();
    const list = [
        {
            title: $t((v) => v.collapse.moreCollapse.componentTool.title),
            data: [
                {
                    components: <QrCode />,
                    icon: qrCodeIcon,
                    title: (
                        <>
                            <span>{$t((v) => v.collapse.moreCollapse.componentTool.qrCode.title)}</span>
                        </>
                    ),
                    description: $t((v) => v.collapse.moreCollapse.componentTool.qrCode.desc),
                    name: QR_CODE,
                },
                {
                    components: <TablePlayer />,
                    icon: tableIcon,
                    title: (
                        <>
                            <span>{$t((v) => v.collapse.moreCollapse.componentTool.table.title)}</span>
                        </>
                    ),
                    description: $t((v) => v.collapse.moreCollapse.componentTool.table.desc),
                    name: TABLE_PLAYER,
                },
            ],
        },

        {
            title: $t((v) => v.collapse.moreCollapse.imageTool.title),
            data: [
                {
                    components: <AiMattingExtension />,
                    icon: aiMattingIcon,
                    title: (
                        <>
                            <span>{$t((v) => v.extensions.aiMatting.title)}</span>
                        </>
                    ),
                    description: $t((v) => v.extensions.aiMatting.desc),
                    name: AI_MATTING_IMAGE,
                },

                {
                    components: <AiExpendImage />,
                    icon: aiExpendIcon,
                    title: (
                        <>
                            <span>{$t((v) => v.collapse.moreCollapse.imageTool.aiExpend.title)}</span>
                        </>
                    ),
                    description: $t((v) => v.collapse.moreCollapse.imageTool.aiExpend.desc),
                    name: AI_EXPEND_IMAGE,
                },

                {
                    components: <AiModifyImage />,
                    icon: aiModifyIcon,
                    title: (
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                            }}
                        >
                            <span>{$t((v) => v.collapse.moreCollapse.imageTool.aiModify.title)}</span>
                            <div className="test">
                                {$t((v) => v.collapse.moreCollapse.imageTool.aiModify.internalTest)}
                            </div>
                        </div>
                    ),
                    description: $t((v) => v.collapse.moreCollapse.imageTool.aiModify.desc),
                    name: AI_MODIFY_IMAGE,
                },
            ].filter(Boolean) as IPluginItem[],
        },
    ];

    if (!displayAiExtend && isImageEditor) {
        list.splice(0, 1);
    }
    return list;
};

const pluginsConfigsMap: { [key: string]: IPluginItem } = {};
for (let i = 0; i < PLUGINS_CONFIGS().length; i++) {
    const item = PLUGINS_CONFIGS()[i];
    item.data.forEach((v) => {
        pluginsConfigsMap[v.name] = v;
    });
}

export const CollapseContext = React.createContext({ entrance: '' });

const MoreCollapse: React.FC = () => {
    const setOpenImageModalType = usePluginModel((state) => state.setOpenImageModalType);
    const setOpenPlugName = usePluginModel((state) => state.setOpenPlugName);
    const { $t } = getLocaleStore();
    const curEntrance = useRef('');
    const [subCollapse, setSubCollapse] = useState<any>();

    const customParams = useSdkModel((state) => state.customParams);
    const displayAiExtend = useDisplayAiExtend();

    const disableAiGenerateImage = true;

    const [demoShow, setDemoShow] = useState(false);

    const openImageModal = (type: string) => {
        const mainLayer = editor.mainLayer.getMainLayer();
        const layer = editor.services.frame.getActivePickLayer(true);

        if (
            mainLayer &&
            !(mainLayer.data as IMainLayerProps).isTransparent &&
            layer?.layerType !== LayerType.IMAGE_LAYER
        ) {
            editor.services.frame.delActiveLayer();
            return false;
        }
        if (layer && layer.layerType === LayerType.IMAGE_LAYER) {
            return false;
        }

        if (type === AI_EXPEND_IMAGE) {
            setOpenImageModalType(AI_EXPEND_IMAGE);
            editor.services.mask.show({
                background: true,
            });
            return true;
        }
        if (type === AI_MODIFY_IMAGE) {
            setOpenImageModalType(AI_MODIFY_IMAGE);
            editor.services.mask.show({
                background: true,
            });
            return true;
        }
        if (type === AI_MATTING_IMAGE) {
            setOpenImageModalType(AI_MATTING_IMAGE);
            editor.services.mask.show({
                background: true,
            });
            return true;
        }
        return false;
    };
    const onClickHandle = async (data: IPluginItem, fromEventTrigger = false, entrance = 'more') => {
        if (!fromEventTrigger && openImageModal(data.name || '')) {
            return;
        }
        curEntrance.current = entrance;
        if (data.extensionInit) {
            await ExtensionManager.launchExtension(data.name);
        }
        setSubCollapse(data.components);
        setOpenPlugName(data.name);
        // if (!fromEventTrigger) {
        //     if (data.trackFunc) {
        //         window._stat_track('tpbj_subfunction_click', {
        //             first_func: 'more',
        //             second_func: data.trackFunc,
        //             func: data.func_id,
        //         });
        //     }
        // }

        // if (data.trackEnter) {
        //     window._stat_track(data.trackEnter.eventName, {
        //         ...data.trackEnter.data,
        //         entrance,
        //     });
        // }
    };

    // 监听插件面板关闭
    useEffect(() => {
        const backHandle = () => {
            setSubCollapse(null);
            setOpenPlugName(null);
        };
        events.on(PLUGIN_CLOSE_EVENT, backHandle);
        return () => {
            events.off(PLUGIN_CLOSE_EVENT, backHandle);
        };
    }, []);

    // 监听打开指定插件面板
    useEffect(() => {
        const onOpenPlugin = ({ func, entrance }: OpenPluginType) => {
            const item = pluginsConfigsMap[func];

            if (item) {
                onClickHandle?.(item, true, entrance);
                editor.services.mask.hide();
            }
        };
        events.on(PLUGIN_OPEN_EVENT, onOpenPlugin);
        return () => {
            events.off(PLUGIN_OPEN_EVENT, onOpenPlugin);
        };
    }, []);

    return (
        <div className="c-more-collapse">
            {subCollapse ? (
                <CollapseContext.Provider value={{ entrance: curEntrance.current }}>
                    <> {subCollapse}</>
                </CollapseContext.Provider>
            ) : (
                <Scrollbars autoHide={true}>
                    <div className="c-more-collapse__menu">
                        {PLUGINS_CONFIGS(
                            displayAiExtend && !customParams.hideSizeExtension,
                            disableAiGenerateImage,
                        ).map((item, index) => {
                            return (
                                <PluginPanel
                                    key={index}
                                    title={item.title}
                                    data={item.data}
                                    onClick={onClickHandle}
                                    iconClick={(item) => {
                                        if (item.name === 'batch_template') {
                                            setDemoShow(true);
                                        }
                                    }}
                                ></PluginPanel>
                            );
                        })}
                    </div>
                </Scrollbars>
            )}
        </div>
    );
};

export default React.memo(MoreCollapse);
